import { useCallback } from 'react'

import { type User, type UserCreateUpdateIntegrations } from '../models/User'
import { type DocumentConnectorIntegration } from '../store/documentConnectors/documentConnectors'
import { useAppDispatch, useAppSelector } from '../store/hooks'
import { selectAccountId } from '../store/auth/authSlice'
import { useSaveExternalUserByIdMutation } from '../store/externalUser/externalUser'
import { addToast } from '../store/toast/toastSlice'
import { TrackEvent, useTracking } from '../apps/professional-app/fa/common/tracking'

export enum DocumentFolderMapSource {
  EDIT_CONTACT = 'editContact',
  IMPORT_DOCUMENT = 'importDocument',
  EXPORT = 'export',
}

export const useLinkContactFolder = (
  contact: User | null,
  source: DocumentFolderMapSource,
  onContactUpdated?: (user: User) => void,
): ((folderIntegration: DocumentConnectorIntegration) => Promise<void>) => {
  const dispatch = useAppDispatch()
  const accountId = useAppSelector(selectAccountId)
  const [saveExternalUser] = useSaveExternalUserByIdMutation()
  const { trackingClient } = useTracking()

  return useCallback(
    async (folderIntegration: DocumentConnectorIntegration) => {
      if (!contact) {
        return
      }

      const otherIntegrations: UserCreateUpdateIntegrations[] = (contact.integrations ?? [])
        .filter((integration) => !(integration.type === 'document' && integration.serviceId === folderIntegration.serviceId))
        .map((integration) => ({
          id: integration.id,
          type: integration.type,
          serviceId: integration.serviceId,
          externalId: integration.externalId,
          externalAccountId: integration.externalAccountId,
        }))

      const newIntegration: UserCreateUpdateIntegrations = {
        type: folderIntegration.type,
        serviceId: folderIntegration.serviceId,
        externalId: folderIntegration.externalId,
        externalAccountId: folderIntegration.externalAccountId ?? '',
      }

      try {
        const response = await saveExternalUser({
          accountId,
          userId: contact.id,
          userData: {
            firstName: contact.firstName,
            lastName: contact.lastName,
            name: contact.name,
            email: contact.email,
            owner: contact.owner ?? null,
            referenceUserId: null,
            integrations: [...otherIntegrations, newIntegration],
          },
        }).unwrap()

        trackingClient.track({
          eventName: TrackEvent.DOCUMENT_FOLDER_MAPPED_TO_CONTACT,
          eventDetails: { system: folderIntegration.serviceId, source },
        })
        onContactUpdated?.(response.data)
      } catch {
        dispatch(addToast({ variant: 'error', title: 'Failed to link folder', messages: ['Please try again.'] }))
      }
    },
    [accountId, contact, dispatch, onContactUpdated, saveExternalUser, source, trackingClient],
  )
}
